import { axios_instance } from "@/api/api";
import type { InfiniteData } from "@tanstack/vue-query";
import { useInfiniteQuery } from "@tanstack/vue-query";
import type { Reactive } from "vue";
import { computed, ref, watch } from "vue";
import type { screenshot } from ".";

interface ScreenshotFilters {
  agent_id: string;
  size: number;
  keyword: string;
}

interface ScreenshotResponse {
  data: screenshot[] | null;
  total_filter: number;
}

type ScreenshotQueryKey = ["screenshots", Omit<ScreenshotFilters, "page">];

const fetchScreenshots = async ({
  queryKey,
  pageParam = 1,
}: {
  queryKey: ScreenshotQueryKey;
  pageParam?: number;
}): Promise<ScreenshotResponse> => {
  const [, filters] = queryKey;

  const config = {
    method: "post",
    maxBodyLength: Infinity,
    url: "/api/v1/view/screenshot",
    headers: {
      "Content-Type": "application/json",
    },
    data: JSON.stringify({
      ...filters,
      page: pageParam,
    }),
  };

  const response = await axios_instance.request<ScreenshotResponse>(config);
  return response.data;
};

export function useScreenshotsInfiniteQuery(
  filters: Reactive<ScreenshotFilters>,
) {
  const queryKey = ref<ScreenshotQueryKey>(["screenshots", filters]);

  watch(
    filters,
    (newFilters) => {
      queryKey.value = ["screenshots", newFilters];
    },
    { deep: true, immediate: true },
  );

  const queryResult = useInfiniteQuery<
    ScreenshotResponse,
    Error,
    InfiniteData<ScreenshotResponse>,
    ScreenshotQueryKey,
    number
  >({
    queryKey: queryKey,
    queryFn: fetchScreenshots,
    initialPageParam: 1,
    getNextPageParam: (
      lastPage: ScreenshotResponse,
      allPages: ScreenshotResponse[],
    ) => {
      const totalItems = lastPage.total_filter;
      const pageSize = filters.size || 20;
      const totalPages = Math.ceil(totalItems / pageSize);
      const currentPage = allPages.length;

      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    staleTime: 5 * 60 * 1000,
  });

  const screenshots = computed(() => {
    const pages = queryResult.data.value?.pages;
    if (!pages) return [];
    return pages.flatMap((page: ScreenshotResponse) => page.data || []);
  });

  const totalFiltered = computed(() => {
    const pages = queryResult.data.value?.pages;
    return pages?.[0]?.total_filter || 0;
  });

  return {
    ...queryResult,
    screenshots,
    totalFiltered,
  };
}
